import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';

/**
 * Get all vendors with optional filters
 */
const getVendors = async (params = {}) => {
  try {
    const queryString = objectToQueryString(params);
    const response = await api.get(`/vendors${queryString}`);
    console.log('[VendorService] getVendors response:', response);
    
    // The API returns data.vendors and meta at root level
    const vendors = response.data?.vendors || [];
    const meta = response.meta || {};
    
    return {
      vendors: vendors,
      meta: {
        totalItems: meta.total || 0,
        totalCount: meta.total || 0,
        totalPages: meta.totalPages || 1,
        currentPage: meta.page || 1,
        pageSize: meta.limit || 20
      }
    };
  } catch (error) {
    console.error('[VendorService] getVendors error:', error);
    throw error;
  }
};

/**
 * Get vendor by ID
 */
const getVendorById = async (id) => {
  try {
    const response = await api.get(`/vendors/${id}`);
    // API returns vendor at response.data.vendor
    return response.data?.vendor || null;
  } catch (error) {
    console.error('Error fetching vendor:', error);
    throw error;
  }
};

/**
 * Create new vendor
 */
const createVendor = async (vendorData) => {
  try {
    const response = await api.post('/vendors', vendorData);
    // API returns vendor at response.data.vendor
    return response.data?.vendor || null;
  } catch (error) {
    console.error('Error creating vendor:', error);
    throw error;
  }
};

/**
 * Quick create vendor (minimal fields)
 */
const quickCreateVendor = async (vendorData) => {
  try {
    const response = await api.post('/vendors/quick-create', vendorData);
    console.log('Quick create vendor response:', response);
    
    // API returns vendor at response.data.vendor
    if (response.data?.vendor) {
      return response.data.vendor;
    } else {
      console.error('Unexpected response structure:', response);
      throw new Error('Invalid response structure from vendor creation');
    }
  } catch (error) {
    console.error('Error quick creating vendor:', error);
    throw error;
  }
};

/**
 * Update vendor
 */
const updateVendor = async (id, vendorData) => {
  try {
    const response = await api.patch(`/vendors/${id}`, vendorData);
    // API returns vendor at response.data.vendor
    return response.data?.vendor || null;
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw error;
  }
};

/**
 * Delete vendor
 */
const deleteVendor = async (id) => {
  try {
    await api.delete(`/vendors/${id}`);
    return true;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
};

/**
 * Search vendors
 */
const searchVendors = async (query, options = {}) => {
  try {
    console.log('[VendorService] searchVendors called with:', query);
    
    // Allow single character search
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const params = {
      q: query.trim(),
      ...options
    };
    
    const queryString = objectToQueryString(params);
    console.log('[VendorService] search URL:', `/vendors/search${queryString}`);
    const response = await api.get(`/vendors/search${queryString}`);
    console.log('[VendorService] searchVendors full response:', response);
    console.log('[VendorService] searchVendors response.data:', response.data);
    
    // API returns vendors at response.data.vendors
    if (response.data?.vendors) {
      console.log('[VendorService] Found vendors at response.data.vendors');
      return response.data.vendors;
    }
    
    console.log('[VendorService] No vendors found, returning empty array');
    return [];
  } catch (error) {
    console.error('[VendorService] searchVendors error:', error);
    return [];
  }
};

/**
 * Get top vendors
 */
const getTopVendors = async (limit = 10) => {
  try {
    const response = await api.get(`/vendors/top?limit=${limit}`);
    // API returns vendors at response.data.vendors
    return response.data?.vendors || [];
  } catch (error) {
    console.error('Error fetching top vendors:', error);
    throw error;
  }
};

/**
 * Check if vendor name exists
 */
const checkVendorName = async (name) => {
  try {
    const response = await api.get(`/vendors/check-name?name=${encodeURIComponent(name)}`);
    console.log('checkVendorName API response:', response);
    // API returns { exists: boolean, vendor: object|null } at response.data
    return response.data || { exists: false, vendor: null };
  } catch (error) {
    console.error('Error checking vendor name:', error);
    return { exists: false, vendor: null };
  }
};

/**
 * Update vendor statistics
 */
const updateVendorStatistics = async (id) => {
  try {
    const response = await api.post(`/vendors/${id}/update-statistics`);
    // API should return vendor at response.data.vendor
    return response.data?.vendor || null;
  } catch (error) {
    console.error('Error updating vendor statistics:', error);
    throw error;
  }
};

const vendorService = {
  getVendors,
  getVendorById,
  createVendor,
  quickCreateVendor,
  updateVendor,
  deleteVendor,
  searchVendors,
  getTopVendors,
  checkVendorName,
  updateVendorStatistics
};

export default vendorService;